import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setItems([]);
      setWishlist([]);
      return;
    }
    setLoading(true);
    try {
      const [c, w] = await Promise.all([api.get("/cart"), api.get("/wishlist")]);
      setItems(c.data.items || []);
      setWishlist(w.data.items || []);
    } catch {
      // network/auth failure — UI will show empty cart; surfacing here would be noisy on page loads
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addToCart = async (product, size, color, qty = 1) => {
    if (!user) {
      toast.error("Sign in to add items to cart");
      return false;
    }
    try {
      await api.post("/cart/add", { product_id: product.id, size, color, qty });
      await refresh();
      toast.success(`${product.name} added to cart`);
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    }
  };

  const updateQty = async (item, qty) => {
    await api.post("/cart/update", {
      product_id: item.product_id, size: item.size, color: item.color, qty,
    });
    await refresh();
  };

  const removeItem = async (item) => {
    await api.post("/cart/remove", {
      product_id: item.product_id, size: item.size, color: item.color, qty: item.qty,
    });
    await refresh();
  };

  const toggleWishlist = async (product) => {
    if (!user) {
      toast.error("Sign in to manage wishlist");
      return;
    }
    try {
      const { data } = await api.post("/wishlist/toggle", { product_id: product.id });
      await refresh();
      toast.success(data.added ? "Added to wishlist" : "Removed from wishlist");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const inWishlist = (productId) => wishlist.some((p) => p.id === productId);

  const cartCount = items.reduce((s, i) => s + i.qty, 0);
  const subtotal = items.reduce((s, i) => s + i.qty * (i.product?.price || 0), 0);

  const value = useMemo(
    () => ({
      items, wishlist, loading, cartCount, subtotal,
      addToCart, updateQty, removeItem, toggleWishlist, inWishlist, refresh,
    }),
    // addToCart/updateQty/removeItem/toggleWishlist/inWishlist close over `refresh`+`user`+`wishlist`
    // so recompute the value whenever any of those state slices change.
    [items, wishlist, loading, cartCount, subtotal, refresh]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
